// Inbox › Tasks (BL-004): the office's tasks and follow-ups. My work by default: your tasks, grouped by
// when they're due (overdue first), with the jobs waiting for your review and the agents' requests
// waiting for your say, each opening where it's handled; then nobody's tasks yet, to pick up;
// everyone's; and the ones closed lately.
// BL-189: on ListPage, its parts PageHead's tabs. My work's first part is the list, the next beside it
// (the aside), and a third under that as a Section; the jobs to review are RowList rows, the agents'
// requests ui-web's ApprovalList, the view Home and an AI tool's chat draw too.

import { useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { ApprovalList, type ApprovalListProps, type Host } from '@flowology/ui-web';
import { RowList } from '@flowology/ui-web/fl';
import { ListPage, Section, type ListRegion, type ListStatus } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { AGENTS_PERMS, PageBanners, REVIEW_PERMS, usePageCrumb, type NavProps } from '../Nav.tsx';
import { holdsAny } from '../shared/may.ts';
import { useQuery } from '../books/ui.tsx';
import { openJob } from '../job/job-page.ts';
import { TaskAdd, TaskEmpty, TaskRows, TaskSaid, useTaskList, type TaskQuery } from './TaskList.tsx';
import { queueChip, type QueueJob } from '../review/review-model.ts';
import type { WaitingApproval } from './tasks-model.ts';

type Part = 'mine' | 'nobody' | 'all' | 'closed';
const PARTS: { part: Part; label: string; query: TaskQuery; empty: string }[] = [
  {
    part: 'mine',
    label: 'My work',
    query: { assignee: 'me' },
    empty: 'Nothing waits on you. Tasks given to you show here.',
  },
  {
    part: 'nobody',
    label: 'Nobody’s yet',
    query: { assignee: 'unassigned' },
    empty: 'Every open task has someone.',
  },
  { part: 'all', label: 'Everyone’s', query: {}, empty: 'No open tasks.' },
  { part: 'closed', label: 'Closed', query: { state: 'closed' }, empty: 'No task has been closed yet.' },
];

const stateOf = (rows: readonly unknown[] | null | undefined, error: string | null): ListStatus =>
  rows ? (rows.length ? 'ready' : 'empty') : error ? 'failed' : 'loading';

export function TasksScreen({
  nav,
  onChanged,
  onOpenReview,
  onOpenApprovals,
}: {
  nav: NavProps;
  onChanged?: () => void;
  /** Review, on this job (My work's jobs waiting for review). */
  onOpenReview?: (jobId: string) => void;
  /** Agents › Requests (My work's requests waiting for a say). */
  onOpenApprovals?: () => void;
}) {
  const timeZone = nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const held = nav.permissions ?? [];
  const reviews = holdsAny(held, REVIEW_PERMS);
  const decides = holdsAny(held, AGENTS_PERMS);
  // My work is everyone's first place: their tasks (with a staff record), review and requests (by permission)
  const parts = nav.staffId || reviews || decides ? PARTS : PARTS.filter((p) => p.part !== 'mine');
  const [part, setPart] = useState<Part>(parts[0]!.part);
  const shown = parts.find((p) => p.part === part)!;
  const mine = part === 'mine';
  // the tasks: yours on My work (only with a staff record), else the tab's
  const withTasks = !mine || !!nav.staffId;
  const list = useTaskList(withTasks ? shown.query : { assignee: 'me' });
  const [said, setSaid] = useState('');
  // My work's other parts ask only while it shows
  const review = useQuery<{ jobs: QueueJob[]; total: number }>(
    'jobs.reviewQueue',
    mine && reviews ? { limit: SHOWN } : null,
  );
  const requests = useQuery<{
    timeZone: string;
    approvals: (WaitingApproval & ApprovalListProps['approvals'][number])[];
    total: number;
  }>('approvals.list', mine && decides ? { status: 'pending', decidable: true, limit: 20 } : null);
  const crumb = usePageCrumb(nav);
  const canAdd = part !== 'closed';
  // read, the list stays ready even with none: Add a task stays under its name, the list's own
  // sentence where its rows would be (as TaskList says it on a record), what was done under it
  const tasks: ListRegion = {
    title: mine ? 'Your tasks' : shown.label,
    status: list.rows ? 'ready' : list.error ? 'failed' : 'loading',
    failedText: list.error ?? 'The tasks didn’t load.',
    what: 'the tasks',
    onRetry: list.reload,
    children: list.rows && (
      <div className="tl">
        {canAdd && (
          <TaskAdd
            key={part}
            about={{}}
            assignee={mine ? (nav.staffId ?? '') : ''}
            onAdded={(s) => {
              setSaid(s);
              list.reload();
              onChanged?.();
            }}
          />
        )}
        {list.rows.length ? (
          <TaskRows
            key={part}
            list={list}
            title={mine ? 'Your tasks' : shown.label}
            query={shown.query}
            timeZone={timeZone}
            canAdd={canAdd}
            groupByDue={part !== 'closed'}
            empty={shown.empty}
            onOpenJob={openJob}
            onSaid={setSaid}
            {...(onChanged ? { onChanged } : {})}
          />
        ) : (
          <TaskEmpty text={shown.empty} />
        )}
        <TaskSaid said={said} />
      </div>
    ),
  };
  // My work's parts, in order: your tasks, the jobs waiting for review, the agents' requests
  const regions: ListRegion[] = mine
    ? [
        ...(nav.staffId ? [tasks] : []),
        ...(reviews ? [reviewRegion(review, onOpenReview)] : []),
        ...(decides ? [requestsRegion(requests, onOpenApprovals)] : []),
      ]
    : [tasks];
  const [first, second, third] = regions as [ListRegion, ListRegion?, ListRegion?];
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <ListPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: 'Tasks',
            lede: 'Follow-ups and callbacks: what needs doing, by when, and who has it.',
            tabs: {
              label: 'Tasks',
              items: parts.map((p) => ({
                label: p.label,
                isCurrent: p.part === part,
                onPress: () => {
                  setPart(p.part);
                  setSaid('');
                },
              })),
            },
          }}
          list={first}
          {...(second
            ? { aside: third ? { ...second, after: <RegionSection region={third} /> } : second }
            : {})}
        />
      </main>
    </div>
  );
}

/** My work lists the oldest few jobs; the rest wait in Review. */
const SHOWN = 10;

/** "And 14 more in Review.": what My work's short list leaves out, counted. */
const moreThere = (shown: number, total: number, where: string) =>
  total > shown ? `And ${total - shown} more in ${where}.` : undefined;

/** My work: the jobs done and waiting for the office's review (jobs.reviewQueue), read only. */
function reviewRegion(
  q: { data: { jobs: QueueJob[]; total: number } | null; error: string | null; reload: () => void },
  onOpen?: (jobId: string) => void,
): ListRegion {
  const jobs = q.data?.jobs ?? [];
  const note = q.data ? moreThere(jobs.length, q.data.total, 'Review') : undefined;
  return {
    title: 'Jobs to review',
    // how many wait in all (the server's count), though only the oldest few are listed
    ...(q.data ? { count: q.data.total } : {}),
    status: stateOf(q.data && jobs, q.error),
    emptyText: 'No job waits for review.',
    failedText: q.error ?? 'The jobs to review didn’t load.',
    what: 'the jobs to review',
    onRetry: q.reload,
    children: (
      <RowList
        label="Jobs to review"
        rows={jobs.map((j) => {
          // as Review's own card says it: ready, or the first thing to fix
          const chip = queueChip(j);
          return {
            id: j.jobId,
            name: `Job #${j.number} · ${j.customerName}`,
            ...(onOpen ? { onOpen: () => onOpen(j.jobId) } : {}),
            lines: [
              chip.tone === 'warn'
                ? { text: chip.text, tone: 'ink' as const, size: 'sm' as const }
                : { text: chip.text },
            ],
          };
        })}
        {...(note ? { note } : {})}
      />
    ),
  };
}

/** ui-web's views in the office: they ask the server as the person, and open places here. */
function officeHost(onApprovals?: () => void): Host {
  return {
    surface: 'office',
    theme: matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
    callTool: (name, args) =>
      client.call(name, args).then(
        (result) => ({ ok: true, result }),
        (e: unknown) => ({
          ok: false,
          code: e instanceof RpcError ? e.code : 'internal',
          message: e instanceof Error ? e.message : 'Something went wrong.',
        }),
      ),
    open: (t) => {
      if (t.kind === 'approvals') onApprovals?.();
    },
  };
}

/** My work: the agents' requests waiting for this person's say (approvals.list), read only. */
function requestsRegion(
  q: {
    data: {
      timeZone: string;
      approvals: (WaitingApproval & ApprovalListProps['approvals'][number])[];
      total: number;
    } | null;
    error: string | null;
    reload: () => void;
  },
  onOpen?: () => void,
): ListRegion {
  // only those this person may decide, chosen and counted by the server (a page filtered here missed some)
  const mine = q.data?.approvals ?? [];
  const note = q.data ? moreThere(mine.length, q.data.total, 'Approvals') : undefined;
  return {
    title: 'Agent requests',
    status: stateOf(q.data && mine, q.error),
    emptyText: 'No agent request waits for your say.',
    failedText: q.error ?? 'The agents’ requests didn’t load.',
    what: 'the agents’ requests',
    onRetry: q.reload,
    children: (
      <>
        <div className="flw flw-office">
          {q.data ? (
            <ApprovalList data={{ timeZone: q.data.timeZone, approvals: mine }} host={officeHost(onOpen)} />
          ) : null}
        </div>
        {note ? <p className="tl__note">{note}</p> : null}
      </>
    ),
  };
}

/** My work's third part, under the second in its column: a Section that says its own state in a muted
 * line, as Time off's Company holidays does. */
function RegionSection({ region: r }: { region: ListRegion }): ReactNode {
  return (
    <Section title={r.title} {...(r.count != null ? { count: r.count } : {})}>
      {r.status === 'ready' ? (
        r.children
      ) : (
        <p className={r.status === 'failed' ? 'tl__bad' : 'tl__note'}>
          {r.status === 'empty' ? r.emptyText : r.status === 'failed' ? r.failedText : `Getting ${r.what}…`}
        </p>
      )}
    </Section>
  );
}
